import {FC, useMemo} from 'react';
import classNames from 'classnames';

import {Box, Col, Row} from 'components/Grid';
import {Typography} from 'components/Typography';
import {ClientOnly} from 'components/ClientOnly';
import Link from 'components/CustomRoutingLink';

import {useContentList} from 'modules/footer/hooks/useContentList';
import {SocialList} from 'modules/footer/components/Footer/SocialList';
import {FooterLocation} from 'modules/footer/components/Footer/Location';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectFooterPopularArticles, selectFooterPopularSections} from 'modules/footer/slice';

import style from './style.module.scss';
import {SUBSCRIBE_DESCRIPTION, SUBSCRIBE_HEADER} from 'modules/footer/constants';
import {EmailSubscribe} from 'modules/footer/components/EmailSubcribe';

interface IFooterLink {
    title: string;
    url: string;
    code?: string;
}

interface IFooterContentItem {
    code: string;
    title: string;
    linkList: IFooterLink[];
    itemClass?: string;
}

export const FooterMain: FC = () => {
    const popularArticles = useAppSelector(selectFooterPopularArticles);
    const popularSections = useAppSelector(selectFooterPopularSections);

    const {desktop: contentListDesktop, tablet: contentListTablet} = useContentList({
        popularArticles: popularArticles ?? [],
        popularSections: popularSections ?? [],
    });

    const emailSubscribeHeader = useMemo(
        () => (
            <div className={style.textWrap}>
                <Typography className={classNames(style.text)} variant={'headline-32-reg-ch'}>
                    {SUBSCRIBE_HEADER}
                </Typography>
                <Typography className={classNames(style.text, style.emailText)} variant={'body-16-r'}>
                    {SUBSCRIBE_DESCRIPTION}
                </Typography>
            </div>
        ),
        [],
    );

    const renderContentList = (contentList: IFooterContentItem[][], isTablet = false) =>
        contentList.map((part, index) => (
            <div key={`footer-menu-part-${index}`} className={style.navPart}>
                {part.map(({code, title, linkList, itemClass}) => (
                    <li
                        key={`${isTablet ? 'tablet-' : 'desktop-'}${code}`}
                        className={classNames(style.navItem, itemClass)}
                    >
                        <Typography className={style.title} variant={'label-14-m'}>
                            {title}
                        </Typography>
                        <div className={style.navSubItem}>
                            {linkList.map((link, id) => (
                                <Link key={`${id}-${link.title}`} href={link.url} className={style.link}>
                                    <Typography
                                        className={classNames(style.text, style.menuFontHeight, {
                                            [style.framedLink]: Boolean(link.code),
                                        })}
                                        variant={'label-16-r'}
                                    >
                                        {link.title}
                                    </Typography>
                                </Link>
                            ))}
                        </div>
                    </li>
                ))}
            </div>
        ));

    const renderContentListDesktop = useMemo(() => renderContentList(contentListDesktop), [contentListDesktop]);

    const renderContentListTablet = useMemo(() => renderContentList(contentListTablet, true), [contentListTablet]);

    return (
        <div className={style.footerMain}>
            <Box>
                <Row className={style.inner} isNoOffset={true}>
                    <Col className={style.nav} lg={7} md={7} sm={12} isNoOffset={true}>
                        {renderContentListDesktop}
                    </Col>
                    <Col className={classNames(style.nav, style.navTablet)} lg={7} md={7} sm={12} isNoOffset={true}>
                        {renderContentListTablet}
                    </Col>
                    <Col className={style.rightBlock} lg={4} md={4} sm={12} isNoOffset={true}>
                        <FooterLocation className={style.location} />
                        <ClientOnly>
                            <EmailSubscribe headerComponent={emailSubscribeHeader} placeholder={'Укажите ваш email'} />
                        </ClientOnly>
                        <div className={'mobile-hidden-block'}>
                            <SocialList />
                        </div>
                    </Col>
                </Row>
            </Box>
        </div>
    );
};
